import { useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaCashRegister, FaTrash } from 'react-icons/fa';
import { addToCart, removeFromCart } from '../slices/cartSlice.js';
import { useGetExchangeRatesQuery } from '../slices/exchangeSlice.js';
import { formatPrice } from '../utils/formatPrice.js';
import { trackEvent } from '../lib/analytics';

function CartScreen() {

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const cart = useSelector((state) => state.cart)
    const { cartItems } = cart

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    async function addToCartHandler(product, qty) {
        dispatch(addToCart({...product, qty}))
    }

    async function removeFromCartHandler(id) {
        dispatch(removeFromCart(id))
    }

    function checkoutHandler() {
        trackEvent('checkout_started', {
          item_count: cartItems.reduce((acc, item) => acc + Number(item.qty || 0), 0),
          free_items: freeItemsCount,
          chargeable_total: chargeableTotal,
        });
        navigate('/login?redirect=/placeorder')
    }


const { data: exchangeData } = useGetExchangeRatesQuery();

const userInfo = useSelector((state) => state.auth.userInfo);
const preferredCurrency = userInfo?.preferredCurrency || 'USD';
const freeItemsCount = cartItems
  .filter((item) => Boolean(item.isFree) || Number(item.price) <= 0)
  .reduce((acc, item) => acc + Number(item.qty || 0), 0);
const paidSubtotal = cartItems
  .filter((item) => !Boolean(item.isFree) && Number(item.price) > 0)
  .reduce((acc, item) => acc + Number(item.qty || 0) * Number(item.price || 0), 0);
const chargeableTotal = paidSubtotal;



  return (
    <div className='grid grid-cols-1 gap-6 lg:grid-cols-12'>
      <section className='lg:col-span-8'>
        <h1 className='mb-5 text-3xl font-semibold text-slate-900'>Shopping Cart</h1>
        {cartItems.length === 0 ? (
          <div className='rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'>
            <p className='m-0 text-sm text-slate-600'>
              Your cart is empty{' '}
              <Link to='/' className='font-semibold text-sky-700 no-underline hover:text-sky-800'>
                Go Back
              </Link>
            </p>
          </div>
        ) : (
          <div className='space-y-3'>
            {cartItems.map((item) => (
              <article
                key={item._id}
                className='grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[96px_minmax(0,1fr)] lg:grid-cols-[96px_minmax(0,1fr)_minmax(110px,auto)_130px_52px] lg:items-center'
              >
                <Link to={`/product/${item._id}`} className='block overflow-hidden rounded-lg border border-slate-100 bg-slate-50'>
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className='h-24 w-full object-cover sm:w-24'
                  />
                </Link>

                <div className='min-w-0'>
                  <Link
                    to={`/product/${item._id}`}
                    className='line-clamp-2 text-base font-semibold text-slate-800 no-underline hover:text-sky-700'
                  >
                    {item.name}
                  </Link>
                  <p className='mt-1 text-xs text-slate-500'>
                    {Boolean(item.isFree) || Number(item.price) <= 0
                      ? 'Free resource'
                      : 'Digital resource added to your account after checkout'}
                  </p>
                </div>

                <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:col-start-2 lg:col-end-6 lg:grid lg:grid-cols-[minmax(110px,auto)_130px_52px] lg:items-center lg:gap-4'>
                  <div className='space-y-1 text-sm text-slate-700'>
                    <p className='m-0 text-xs font-medium uppercase tracking-wide text-slate-500'>
                      Price
                    </p>
                    <p className='m-0 font-semibold text-slate-700'>
                      {Boolean(item.isFree) || Number(item.price) <= 0
                        ? 'Free'
                        : formatPrice(item.price, preferredCurrency, exchangeData)}
                    </p>
                  </div>

                  <div className='flex items-center gap-2'>
                    <label className='text-xs font-medium uppercase tracking-wide text-slate-500'>
                      Licenses
                    </label>
                    <select
                      aria-label={`Licenses for ${item.name}`}
                      value={item.qty}
                      onChange={(e) => addToCartHandler(item, Number(e.target.value))}
                      className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100'
                    >
                      {[...Array(10).keys()].map((ele) => (
                        <option key={ele + 1} value={ele + 1}>
                          {ele + 1}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type='button'
                    onClick={() => removeFromCartHandler(item._id)}
                    className='inline-flex h-10 w-10 items-center justify-center rounded-lg bg-rose-600 text-white transition hover:bg-rose-700'
                    aria-label='Remove from cart'
                  >
                    <FaTrash />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <aside className='lg:col-span-4'>
        <div className='rounded-2xl border border-slate-200 bg-white p-5 shadow-sm'>
          <h2 className='mb-4 text-xl font-semibold text-slate-900'>
            Total ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
          </h2>
          <div className='space-y-2 text-sm text-slate-700'>
            <div>
              <strong>Total:</strong>{' '}
              {chargeableTotal <= 0
                ? 'Free'
                : formatPrice(chargeableTotal, preferredCurrency, exchangeData)}
            </div>
            {freeItemsCount > 0 && (
              <div>
                <strong>Includes free items:</strong> {freeItemsCount}
              </div>
            )}
          </div>
          {freeItemsCount > 0 && chargeableTotal > 0 && (
            <div className='mt-4 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm leading-6 text-sky-800'>
              This cart includes both free and paid items. Only the paid total is charged at checkout,
              and the free items are added to your account after the paid checkout succeeds.
            </div>
          )}
          <p className='mt-4 text-xs leading-5 text-slate-500'>
            Digital products are added to your account after checkout so you can access them later from Purchase History.
          </p>
          <button
            type='button'
            disabled={cartItems.length === 0}
            onClick={checkoutHandler}
            className='mt-5 inline-flex w-full items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300'
          >
            <FaCashRegister className="mr-2" />
            Proceed to Checkout
          </button>
        </div>
      </aside>
    </div>
  )
}

export default CartScreen
